// Dashboard.js - Main dashboard component
import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { crmService } from '../../services/crmService.js';
import { RealtimeService } from '../../services/realtimeService.js';
import { useTheme, BACKGROUNDS, patternInk } from '../../contexts/ThemeContext.jsx';
import ChatNotificationPopup from '../chat/ChatNotificationPopup.jsx';

// Chunk file names change on every deploy. A tab opened before a deploy still
// references the old names, so switching views fails with a blank screen.
// Reload once to pick up the new build instead of rendering nothing.
const CHUNK_RELOAD_KEY = 'crm_chunk_reload';
const lazyWithReload = (importer) => lazy(() =>
    importer().then((mod) => {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        return mod;
    }).catch((error) => {
        if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
            sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
            window.location.reload();
            return new Promise(() => {});
        }
        throw error;
    })
);

// Heavy views loaded on demand — keeps initial bundle small
const DashboardHome       = lazyWithReload(() => import('./DashboardHome.jsx'));
const AgentLeadManagement = lazyWithReload(() => import('../leads/AgentLeadManagement.jsx'));
const AdminLeadManagement = lazyWithReload(() => import('../leads/AdminLeadManagement.jsx'));
const AdminPanel          = lazyWithReload(() => import('../admin/AdminPanel.jsx'));
const AgentDocuments      = lazyWithReload(() => import('../agent/AgentDocuments.jsx'));
const AgentTasks          = lazyWithReload(() => import('../agent/AgentTasks.jsx'));
const ChatComponent       = lazyWithReload(() => import('../chat/ChatComponent.jsx'));
const EmailDashboard      = lazyWithReload(() => import('../email/EmailDashboard.jsx'));
const Personalize         = lazyWithReload(() => import('../settings/Personalize.jsx'));

// Idle timeout duration (30 minutes in milliseconds)
const IDLE_TIMEOUT = 30 * 60 * 1000;

const getService = (name) => {
    if (name === 'crmService') return crmService;
    if (name === 'RealtimeService') return RealtimeService;
    return null;
};

// Dashboard component that renders the AgentLeadManagement component for agents
function Dashboard({ user, onLogout }) {
    const { theme, bgKey, customBgUrl, isDark } = useTheme();
    // Restore last view from localStorage or default to 'dashboard'
    const [currentView, setCurrentView] = useState(() => {
        if (user.role === 'tech') return 'email';
        const savedView = localStorage.getItem('crm_current_view');
        return savedView || 'dashboard';
    });


    // Idle timeout tracking
    const [lastActivity, setLastActivity] = useState(Date.now());

    // Superadmin's cross-team switcher - everyone else never touches this
    const [selectedTeamId, setSelectedTeamId] = useState(() => localStorage.getItem('crm_selected_team') || 'all');
    const [allTeams, setAllTeams] = useState([]);

    useEffect(() => {
        if (user.role !== 'superadmin') return;
        crmService.getTeams().then(setAllTeams).catch(() => setAllTeams([]));
    }, [user.role]);

    useEffect(() => {
        localStorage.setItem('crm_selected_team', selectedTeamId);
    }, [selectedTeamId]);

    // Everyone except superadmin is locked to their own team's data.
    // Superadmin's switcher controls this — 'all' means no filter (cross-team view).
    const effectiveTeamId = user.role === 'superadmin'
        ? (selectedTeamId === 'all' ? null : selectedTeamId)
        : (user.team_id || null);

    // Save current view to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('crm_current_view', currentView);
    }, [currentView]);

    // Reset activity timestamp on user interaction
    const resetActivity = useCallback(() => {
        setLastActivity(Date.now());
    }, []);

    // Set up activity listeners
    useEffect(() => {
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

        events.forEach(event => {
            document.addEventListener(event, resetActivity, { passive: true });
        });

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, resetActivity);
            });
        };
    }, [resetActivity]);

    // Check for idle timeout every minute
    useEffect(() => {
        const checkIdle = setInterval(() => {
            const now = Date.now();
            if (now - lastActivity > IDLE_TIMEOUT) {
                // Save current view before logout so user returns here
                localStorage.setItem('crm_current_view', currentView);
                alert('Session expired due to inactivity. Please log in again.');
                onLogout();
            }
        }, 60000); // Check every minute

        return () => clearInterval(checkIdle);
    }, [lastActivity, currentView, onLogout]);

    // Initialize realtime service
    useEffect(() => {
        const RealtimeService = getService('RealtimeService');
        if (RealtimeService) {
            RealtimeService.init();
            console.log('[Dashboard] Realtime service initialized');
        }

        return () => {
            // Cleanup on logout
            if (RealtimeService) {
                RealtimeService.cleanup();
            }
        };
    }, []);

    // Define navigation items based on user role
    const getNavItems = () => {
        const common = [
            { id: 'dashboard', label: 'DASHBOARD' }
        ];

        const agentItems = [
            { id: 'leads', label: 'LEADS' },
            { id: 'documents', label: 'DOCUMENTS' },
            { id: 'tasks', label: 'TASKS' }
        ];

        const adminItems = [
            { id: 'leads', label: 'LEADS' },
            { id: 'documents', label: 'DOCUMENTS' },
            { id: 'tasks', label: 'TASKS' },
            { id: 'admin', label: 'ADMIN PANEL' },
            { id: 'email', label: 'EMAIL' }
        ];

        const personalise = { id: 'personalize', label: 'PERSONALISE' };

        switch (user.role) {
            case 'admin':
            case 'superadmin':
                return [...common, ...adminItems, personalise];
            case 'agent':
                return [...common, ...agentItems, personalise];
            case 'manager':
                return [...common, ...adminItems, personalise];
            case 'tech':
                return [{ id: 'email', label: 'EMAIL' }, personalise];
            default:
                return [...common, personalise];
        }
    };

    // Chat state
    const [chatOpen, setChatOpen] = useState(false);
    const [chatUnreadTotal, setChatUnreadTotal] = useState(0);
    const [initialConversation, setInitialConversation] = useState(null);

    // Chat popup notifications
    const [chatNotifications, setChatNotifications] = useState([]);
    const prevDMCounts = useRef({});
    const prevGroupCounts = useRef({});
    const seenNotifIds = useRef(new Set());

    const dismissNotification = useCallback((id) => {
        setChatNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const handleNotificationOpen = useCallback((notif) => {
        dismissNotification(notif.id);
        if (notif.type === 'dm') {
            setInitialConversation({ type: 'dm', username: notif.senderUsername });
        } else {
            setInitialConversation({ type: 'group', groupId: notif.groupId });
        }
        setChatOpen(true);
    }, [dismissNotification]);

    const makeInitials = (display) => {
        const parts = display.trim().split(' ');
        return parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : display.slice(0, 2).toUpperCase();
    };

    // Poll for unread chat count and detect new messages for notifications
    useEffect(() => {
        const loadUnread = async () => {
            try {
                const svc = getService('crmService');
                if (!svc) return;

                const [dmCounts, groupCounts] = await Promise.all([
                    svc.getUnreadCounts(user.username),
                    svc.getUnreadGroupCounts(user.username),
                ]);

                const dmTotal = Object.values(dmCounts).reduce((s, c) => s + c, 0);
                const gTotal = Object.values(groupCounts).reduce((s, c) => s + c, 0);
                setChatUnreadTotal(dmTotal + gTotal);

                // Detect increases → fetch latest messages → push notifications
                const hasNewDM = Object.entries(dmCounts).some(
                    ([sender, count]) => count > (prevDMCounts.current[sender] || 0)
                );
                const hasNewGroup = Object.entries(groupCounts).some(
                    ([gid, count]) => count > (prevGroupCounts.current[gid] || 0)
                );

                if (hasNewDM || hasNewGroup) {
                    const latest = await svc.getLatestUnreadMessages(user.username);
                    const newNotifs = [];

                    for (const msg of (latest.dms || [])) {
                        const notifId = `dm-${msg.sender}-${msg.id}`;
                        if (seenNotifIds.current.has(notifId)) continue;
                        // Skip if chat is already open at this DM
                        if (chatOpen && initialConversation?.type === 'dm' && initialConversation?.username === msg.sender) continue;
                        seenNotifIds.current.add(notifId);
                        newNotifs.push({
                            id: notifId,
                            type: 'dm',
                            senderUsername: msg.sender,
                            senderDisplay: msg.sender,
                            senderInitials: makeInitials(msg.sender),
                            content: msg.content,
                            hasFile: !!msg.file_url,
                            groupName: null,
                            groupId: null,
                        });
                    }

                    for (const msg of (latest.groups || [])) {
                        const notifId = `group-${msg.group_id}-${msg.id}`;
                        if (seenNotifIds.current.has(notifId)) continue;
                        if (chatOpen && initialConversation?.type === 'group' && initialConversation?.groupId === msg.group_id) continue;
                        seenNotifIds.current.add(notifId);
                        newNotifs.push({
                            id: notifId,
                            type: 'group',
                            senderUsername: msg.sender,
                            senderDisplay: msg.sender,
                            senderInitials: makeInitials(msg.sender),
                            content: msg.content,
                            hasFile: !!msg.file_url,
                            groupName: msg.group_name,
                            groupId: msg.group_id,
                        });
                    }

                    if (newNotifs.length > 0) {
                        setChatNotifications(prev => [...prev, ...newNotifs].slice(-5)); // cap at 5
                    }
                }

                prevDMCounts.current = dmCounts;
                prevGroupCounts.current = groupCounts;
            } catch (err) { /* ignore */ }
        };
        loadUnread();
        const interval = setInterval(loadUnread, 10000);
        return () => clearInterval(interval);
    }, [user.username, chatOpen, initialConversation]);

    const navItems = getNavItems();

    // Render content based on current view — all components are React.lazy, Suspense handles loading
    const renderContent = () => {
        if (currentView === 'dashboard') return <DashboardHome user={user} teamId={effectiveTeamId} />;

        if (currentView === 'My Leads' || currentView === 'leads') {
            return user.role === 'agent'
                ? <AgentLeadManagement user={user} />
                : <AdminLeadManagement user={user} selectedTeamId={effectiveTeamId} />;
        }

        if (currentView === 'Admin Leads' && (user.role === 'admin' || user.role === 'manager'))
            return <AdminLeadManagement user={user} selectedTeamId={effectiveTeamId} />;

        if (currentView === 'admin' && (user.role === 'admin' || user.role === 'manager' || user.role === 'superadmin'))
            return <AdminPanel user={user} selectedTeamId={effectiveTeamId} />;

        if (currentView === 'documents') return <AgentDocuments user={user} teamId={effectiveTeamId} />;
        if (currentView === 'tasks')     return <AgentTasks user={user} teamId={effectiveTeamId} />;

        if (currentView === 'email' && (user.role === 'admin' || user.role === 'manager' || user.role === 'superadmin' || user.role === 'tech'))
            return <EmailDashboard user={user} />;

        if (currentView === 'personalize') return <Personalize />;

        return null;
    };

    // User initials for avatar
    const userInitials = user.first_name && user.last_name
        ? (user.first_name[0] + user.last_name[0]).toUpperCase()
        : user.username.slice(0, 2).toUpperCase();

    // Build body background style with optional pattern overlay
    const bgPat = BACKGROUNDS[bgKey];
    const bodyBgStyle = { minHeight: '100vh', background: theme.bodyBg };
    if (bgKey === 'custom' && customBgUrl) {
        bodyBgStyle.backgroundImage = `url(${customBgUrl})`;
        bodyBgStyle.backgroundSize = 'cover';
        bodyBgStyle.backgroundPosition = 'center';
        bodyBgStyle.backgroundAttachment = 'fixed';
    } else if (bgPat?.value) {
        const pattern = bgPat.value(patternInk(isDark));
        if (pattern) {
            bodyBgStyle.backgroundImage = pattern;
            if (bgPat.size) bodyBgStyle.backgroundSize = bgPat.size;
        }
    }

    return (
        <div style={bodyBgStyle}>
            {/* ── Header ───────────────────────────────────────────────── */}
            <header style={{
                background: theme.headerBg,
                borderBottom: `1px solid ${theme.headerBorder}`,
                boxShadow: `0 2px 8px ${theme.shadow}`,
            }}>
                <div style={{ width: '100%', padding: '0 28px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '60px' }}>

                        {/* Logo */}
                        <h1 style={{ margin: 0, lineHeight: 1 }}>
                            <span style={{ fontSize: '22px', fontWeight: '900', color: '#cbd5e1', letterSpacing: '-0.5px' }}>$</span>
                            <span style={{ fontSize: '22px', fontWeight: '700', color: '#fff', letterSpacing: '-0.5px' }}>tatic</span>
                            <span style={{ fontSize: '22px', fontWeight: '300', color: '#64748b', letterSpacing: '-0.5px' }}> CRM</span>
                        </h1>

                        {/* Right side */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {/* Team switcher - superadmin only */}
                            {user.role === 'superadmin' && (
                                <select
                                    value={selectedTeamId}
                                    onChange={(e) => setSelectedTeamId(e.target.value)}
                                    title="Choose which team's data to view"
                                    style={{
                                        padding: '7px 10px', borderRadius: '6px',
                                        background: theme.headerBorder, color: theme.text,
                                        border: `1px solid ${theme.headerBorder}`,
                                        fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                                    }}
                                >
                                    <option value="all">Viewing: All Teams</option>
                                    {allTeams.map((team) => (
                                        <option key={team.id} value={team.id}>Viewing: {team.name}</option>
                                    ))}
                                </select>
                            )}

                            {/* User info */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                    width: '32px', height: '32px', borderRadius: '50%',
                                    background: `linear-gradient(135deg, ${theme.headerBorder}, ${theme.accent})`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '12px', fontWeight: '700', color: '#fff',
                                }}>
                                    {userInitials}
                                </div>
                                <div style={{ lineHeight: 1.2 }}>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: theme.text }}>
                                        {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username}
                                    </div>
                                    <div style={{ fontSize: '10px', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {user.role}
                                    </div>
                                </div>
                            </div>

                            <div style={{ width: '1px', height: '28px', background: theme.headerBorder }} />

                            {/* Chat button — hidden for tech role */}
                            {user.role !== 'tech' && <button
                                onClick={() => setChatOpen(!chatOpen)}
                                style={{
                                    position: 'relative',
                                    padding: '7px 14px', borderRadius: '6px',
                                    background: chatOpen ? theme.headerBorder : `${theme.accent}30`,
                                    color: theme.accent, border: `1px solid ${theme.headerBorder}`,
                                    fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                💬 Chat
                                {chatUnreadTotal > 0 && (
                                    <span style={{
                                        position: 'absolute', top: '-6px', right: '-6px',
                                        background: '#ef4444', color: '#fff', borderRadius: '50%',
                                        width: '18px', height: '18px', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', fontSize: '10px', fontWeight: '700',
                                    }}>
                                        {chatUnreadTotal > 99 ? '99+' : chatUnreadTotal}
                                    </span>
                                )}
                            </button>}

                            {/* Logout */}
                            <button
                                onClick={onLogout}
                                style={{
                                    padding: '7px 14px', borderRadius: '6px',
                                    background: 'transparent', color: theme.textMuted,
                                    border: `1px solid ${theme.headerBorder}`,
                                    fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                                }}
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* ── Navigation ───────────────────────────────────────────── */}
            <nav style={{
                background: theme.navBg,
                borderBottom: `1px solid ${theme.navBorder}`,
                boxShadow: `0 1px 3px ${theme.shadow}`,
            }}>
                <div style={{ width: '100%', padding: '0 28px' }}>
                    <div style={{ display: 'flex', gap: '0' }}>
                        {navItems.map((item) => {
                            const active = currentView === item.id;
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => setCurrentView(item.id)}
                                    style={{
                                        padding: '14px 22px',
                                        background: 'transparent',
                                        color: active ? theme.navActive : theme.navText,
                                        border: 'none',
                                        borderBottom: active ? '2px solid transparent' : '2px solid transparent',
                                        borderImage: active ? `${theme.indicator} 1` : 'none',
                                        fontSize: '11px',
                                        fontWeight: active ? '700' : '600',
                                        cursor: 'pointer',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        transition: 'color 0.15s',
                                        marginBottom: '-1px',
                                    }}
                                >
                                    {item.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </nav>

            {/* ── Main Content ─────────────────────────────────────────── */}
            {/* index.css carries ~140 `#crm-content.dark` rules that recolour the
                hard-coded white panels inside these views. Nothing ever applied the
                class, so every dark theme left white cards white. */}
            <main id="crm-content" className={isDark ? 'dark' : ''} style={{ width: '100%', padding: '24px' }}>
                <Suspense fallback={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: theme.textMuted, fontSize: '14px' }}>
                        Loading…
                    </div>
                }>
                    {renderContent()}
                </Suspense>
            </main>

            {/* Chat Panel — hidden for tech role */}
            {user.role !== 'tech' && (
                <Suspense fallback={null}>
                    <ChatComponent
                        user={user}
                        isOpen={chatOpen}
                        onClose={() => { setChatOpen(false); setInitialConversation(null); }}
                        initialConversation={initialConversation}
                        teamId={effectiveTeamId}
                    />
                </Suspense>
            )}

            {/* Chat Popup Notifications — hidden for tech role */}
            {user.role !== 'tech' && <ChatNotificationPopup
                notifications={chatNotifications}
                onDismiss={dismissNotification}
                onOpen={handleNotificationOpen}
            />}
        </div>
    );
}

export default Dashboard;